import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import * as globals from '../../globals.js'
import { actionCreators as optimActionCreators } from '../../store/Optim.ts'
import { actionCreators as studyActionCreators } from '../../store/Study.ts'
import { PageHeader } from '../common/PageHeader.js'
import { FilterChooser } from '../filter/FilterChooser.js'
import './Optimizer.scss'
import { OptimizeEditorMulti } from './OptimizeEditorMulti.js'
import { getMeta } from '../../utils/metaUtils'
import { format as d3Format } from 'd3-format';



export const Optimizer = (props) => {

    const { studyId } = useParams()
    const [spinnerIndex, setSpinnerIndex] = useState(0)
    const [showSection, setShowSection] = useState('target')
    const [costs, setCosts] = useState({})
    const [metricConstraints, setMetricConstraints] = useState({})

    const study = useSelector(state => state.study)
    const auth = useSelector(state => state.auth)
    const optim = useSelector(state => state.optimizer)
    const activeModel = props?.activeConfig
    const model = useSelector(state => state.model)
    const atts = model?.[activeModel]?.config.productDef.atts
        
    const dispatch = useDispatch()    

    if (!optim) return null 
    
    const format = d3Format(".2f")
    
    useEffect(() => {
        (async () => {
            try {
                // Load level costs
                const metaTable = optim.goal.targetCosts && Object.keys(optim.goal.targetCosts).length > 0
                    ? optim.goal.targetCosts
                    : await getMeta(study.uid, auth, 'levelcosts')
                setCosts(metaTable)
                dispatch(optimActionCreators.setFeatureCosts(metaTable))

                // Load metric constraints
                const metaConstraints = optim.goal.metricConstraints && Object.keys(optim.goal.metricConstraints).length > 0
                    ? optim.goal.metricConstraints
                    : await getMeta(study.uid, auth, 'metricConstraints')
                setMetricConstraints(metaConstraints || []) // Ensure it's an array
                dispatch(optimActionCreators.setMetricConstraints(metaConstraints))

            } catch (err) {
                console.error(err)
            }

            // Initialize the products
            dispatch(optimActionCreators.initProdsToOptimize(activeModel))
        })()
    }, [dispatch, activeModel])

    // Separate effect that waits for products to be initialized
    useEffect(() => {
        if (optim.config.products && optim.config.products.length > 0 && (!optim.goal.targetProducts || optim.goal.targetProducts.length === 0)) {
            dispatch(optimActionCreators.addOptimizationSelection(optim.config.products[0]))
        }
    }, [optim.config.products, optim.goal.targetProducts, dispatch])


    // const optim = config.optim || {}
    const prods_to_opt = optim.goal.targetProducts || []
    const selection = prods_to_opt && prods_to_opt.length > 0 ? prods_to_opt[0].uid : ''   

    const options = {
        selections: prods_to_opt,
        activeConfig: props?.activeConfig,
        vehImages: props?.vehImages,
        productLabel: study.productLabel,
        filterdatasetId: props?.filtersDatasetId
    }

    const optRunOptions = {
        path: props?.loadOptScnToPage
    }

    const selMetric = optim.goal.targetMeasure || globals.default_optimizer_metric
    const spinnerSel = prods_to_opt.find((sel, idx) => idx === spinnerIndex)

    const optimizeClicked = () => {
        if (prods_to_opt.length === 0) return
        dispatch(optimActionCreators.runOptimizer(activeModel))
        const opt = {
            path: options?.loadOptPath
        }
        window.topBar.openMenu('optruns', opt)
    }

    const setOptimizationMetric = (metric) => {
        dispatch(optimActionCreators.setOptimizationMetric(metric))
    }

    const handleCosts = (attname, level, cost) => {
        const updatedCosts = { ...costs }
        
        if (updatedCosts[attname]) {
            const levelToUpdate = updatedCosts[attname].find((lev) => lev.label === level)
            if (levelToUpdate) {
                levelToUpdate.value = cost 
            }
        }
        else {
            console.warn(`Attribute '${attname}' not found in costs.`)
        }

        setCosts(cost)
        dispatch(optimActionCreators.setFeatureCosts(costs))
    }


    const handleKeyDown = (event, attname, level) => {
        if (event.key === 'Enter') {
            handleCosts(attname, level, event.target.value)
        }
    }

    const handleAddConstraint = () => {
        setMetricConstraints([...metricConstraints, { targetMetric: '', constraintMetric: '', operator: '', value: '' }])
    }

    const handleConstraintChange = (index, key, value) => {
        const updatedConstraints = [...metricConstraints]
        updatedConstraints[index][key] = value
        setMetricConstraints(updatedConstraints)
        dispatch(optimActionCreators.setMetricConstraints(metricConstraints))
    }


    const maxRows = costs && Object.keys(costs).length > 0 ?  Math.max(...Object.keys(costs).map(att => costs[att].length || 0)) : 1

    // Define styles for consistent row height based on max rows
    const rowHeight = 30 // Set a row height in pixels (adjust as needed)
    const tableHeight = rowHeight * maxRows // Total table height
    
    return (
        <div className="optimize">
            <div className='topSection'>
                {/* Three Boxes */}
                <div className='boxStyle' onClick={() => setShowSection('target')}>
                    <i className='icon fal fa-bullseye' style={{ fontSize: 24 }} />
                    <div className='title'>Target</div>
                    <p className='description'>Set optimization goals for your target.</p>
                </div>
                <div className='boxStyle' onClick={() => setShowSection('constraints')}>
                    <i className='icon fal fa-balance-scale' style={{ fontSize: 24 }} />
                    <div className='title'>Constraints</div>
                    <p className='description'>Define constraints for optimization.</p>
                </div>
                <div className='boxStyle' onClick={() => setShowSection('costs')}>
                    <i className='icon fal fa-dollar-sign' style={{ fontSize: 24 }} />
                    <div className='title'>Costs</div>
                    <p className='description'>Set cost parameters for optimization.</p>
                </div>
            </div>
            
            <div className='mainSection'>
                {/* Main Content based on selected box */}
                {showSection === 'target' && (
                    <div>
                        <div>
                            <h3>Select goal</h3>
                            <div >
                                <select value={selMetric} onChange={(e) => setOptimizationMetric(e.target.value)} style={{height:30, width:200}}>
                                    {globals.metrics.map(metric => 
                                        <option key={metric.key} value={metric.key}>{metric.label}</option>
                                    
                                    // <span key={metric.key}
                                    //     className={'item ' + (metric.key === selMetric ? ' sel' : '')}
                                    //     onClick={() => setOptimizationMetric(metric.key)}
                                    // >
                                    //     Maximize {metric.label}
                                    // </span>
                                    )}
                                </select>
                            </div>
                        </div>           

                        <div style={{marginTop:20}}>
                            <h3>Select target audience</h3>
                            <FilterChooser style={{height:30, width:200, fontSize:'9pt', borderColor: '#eaeaea', backgroundColor:'#325373'}} />
                        </div>
                    </div>
                )}
                {showSection === 'constraints' && (
                    <>
                    <div style={{width:250}}>
                            <div className="metric-constraints">
                                <div className='metric-titlebar'>                                   
                                    <h3>Metric Constraints</h3>
                                     <button id='add-metric' className='btn border drk' onClick={handleAddConstraint} >
                                        + Add
                                    </button>
                                </div>
                            {metricConstraints.length > 0 ? (
                                metricConstraints.map((mc, idx) => (
                                    <div key={idx} className='constraint-container'>

                                        <div className='inner-container'>
                                            <span>When target metric is:</span>
                                            <select
                                                value={mc.targetMetric}
                                                onChange={(e) => handleConstraintChange(idx, 'targetMetric', e.target.value)}
                                                className='met-select'
                                            >
                                                {globals.metrics.map((metric) => (
                                                    <option key={metric.key} value={metric.key}>
                                                        {metric.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className='inner-container'>
                                            <span>I would like to constrain metric:</span>
                                            <select
                                                value={mc.constraintMetric}
                                                onChange={(e) => handleConstraintChange(idx, 'constraintMetric', e.target.value)}
                                                className='met-select'
                                            >
                                                {globals.metrics.map((metric) => (
                                                    <option key={metric.key} value={metric.key}>
                                                        {metric.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className='inner-container'>
                                           <span> When it is:</span>
                                            <select
                                                value={mc.operator}
                                                onChange={(e) => handleConstraintChange(idx, 'operator', e.target.value)}
                                                className='met-select'
                                            >
                                                <option value="gt">{'>'}</option>
                                                <option value="gte">{'>='}</option>
                                                <option value="lt">{'<'}</option>
                                                <option value="lte">{'<='}</option>
                                                <option value="eq">{'='}</option>
                                            </select>
                                        </div>
                                        <div className='inner-container'>
                                            <span>The value:</span>
                                            <input
                                                type="text"
                                                value={mc.value}
                                                onChange={(e) => handleConstraintChange(idx, 'value', e.target.value)}
                                                className='met-select'
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div>No constraints found. Add a new one below.</div>
                            )}
                        </div>
                        
                    </div>
                    <div onClick={ () => setShowSection('constraints')} style={{width:'100%'}}>
                        <OptimizeEditorMulti {...options} />
                    </div>
                    </>
                )}
                {showSection === 'costs' && (
                    <div onClick={() => setShowSection('costs')} style={{ padding: '20px 30px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px' }}>
                    <center>
                        <h3>Feature Costs</h3>
                    </center>
                    
                    <div className='costs'>
                            {Object.keys(costs).filter(c => atts.some(att => att.name === c)).map(att =>
                            (
                                <div key={att} style={{ display: 'flex', justifyContent: 'center', height: tableHeight, alignItems: 'stretch' }}>
                                    <table className='tableCosts'>
                                        <tbody>
                                            {/* Attribute name row */}
                                            <tr>
                                                <td colSpan="2" className='attHeader'>
                                                    {atts?.find(f => f.name === att)?.label}
                                                </td>
                                            </tr>
                                            
                                            {/* Cost input rows */}
                                            {costs[att].map((level, index) => (
                                                <tr key={`${att}-${index}`} style={{ height: `${rowHeight}px` }}> {/* Set row height */}
                                                    <td style={{ width: '270px', color: "#808080", paddingLeft: "20px", marginRight: 10, borderBottom: level.label ? '1px dashed #dfdfdf' : 'none' }}>
                                                        {level.label}
                                                    </td>
                                                    <td>
                                                        <span className='dollar'>$</span>
                                                        <input
                                                            type="text"
                                                            defaultValue={level.value}
                                                            className='costInput'
                                                            // onChange={ev => onChangeLevelCosts(att, level.label, ev.target.value)}
                                                            onKeyDown={(ev) => handleKeyDown(ev, att, level.label)}
                                                            onBlur={(ev) => handleCosts(att, level.label, ev.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            
                                            
                                            {/* Add empty rows to match max row height */}
                                            {Array.from({ length: maxRows - costs?.[att]?.length }).map((_, i) => (
                                                <tr key={`empty-${att}-${i}`} style={{ height: `${rowHeight}px` }}>
                                                    <td colSpan="2" style={{ borderBottom: 'none' }}>&nbsp;</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                    </div>
                </div>

                )}
            </div>

            <div className='bottomSection'>
                <div className='opt-button'>
                    {prods_to_opt.length === 0 ?
                        <div className='btn-note'>
                            (Select target {study.productLabel}(s) first)
                        </div>
                        : null
                    }
                    <div>
                        <span className={'btn border drk' + (prods_to_opt.length === 0 ? ' disabled' : '')} style={{display:'inline-block'}} onClick={optimizeClicked}>
                            <i className='fal fa-cog'/> Optimize
                        </span>
                    </div>
                </div>
                 <div>
                    <span className='btn border drk' style={{display:'inline'}} onClick={() => window.topBar.openMenu('optruns', optRunOptions)}>
                        Open Optimizations Runs
                    </span>
                </div>
            </div>
        </div>
    )
}

// {
                                            //     costs?.[att]?.map((lev, index) => {

                                            //         <tr style={{ height: `${rowHeight}px` }}>
                                            //             <td className='sectionCostHeader'>
                                            //                 { lev.label}
                                            //             </td>
                                            //             <td>
                                            //                 <span className='dollar'>$</span>
                                            //                 <input
                                            //                     type="text"
                                            //                     value={format(lev.value)}
                                            //                     className='costInput'
                                            //                     onKeyDown={(ev) => handleKeyDown(ev, att, level.label)}
                                            //                     onBlur={(ev) => handleCosts(att, level.label, ev.target.value)}
                                            //                 />
                                            //             </td>
                                            //         </tr>
                                            //     })
                                            // }